import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom, from, map, Observable } from 'rxjs';
import { Department } from '../models/department.model';
import { DepartmentCategory } from '../models/department_category.model';
import { Role } from '../models/role.model';
import { ApiService } from './api.service';
import { UserService } from './user.service';
import { UtilsService } from './utils.service';
import { ArrayResponse } from '../models/array_response.interface';
import { MyCompanyProductRoles } from '../models/my_company_product_roles.model';
import { MyRoles } from '../models/my_roles.model';
import { CompanyProductTeam } from '../models/company_product_team.model';
import { CompanyProductUserMemberRoleEnum } from '../enums/company-product-user-member-role.enum';
import { CompanyUserMemberRoleEnum } from '../enums/company-user-member-role.enum';
import { User } from '../models/user.model';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private current_role: Role | null = null;
  private current_department_categories: DepartmentCategory[] | undefined;
  private current_departments: Department[] | undefined;
  private current_departments_subject = new BehaviorSubject<Department[]>([]);

  private roles = new BehaviorSubject<{ [key: string]: Role } | null>(null);
  private departments = new BehaviorSubject<{ [key: string]: Department } | null>(null);
  private departmentCategories = new BehaviorSubject<{
    [key: string]: DepartmentCategory;
  } | null>(null);
  public rolesObservable = this.roles.asObservable();
  public departmentsObservable = this.departments.asObservable();
  public departmentCategoriesObservable = this.departmentCategories.asObservable();
  public current_departments_observable = this.current_departments_subject.asObservable();

  // initiating observables
  public fetchingRolesPromise: Promise<Role[]> | null = null;
  public fetchingDepartmentsPromise: Promise<ArrayResponse<Department>> | null = null;
  public fetchingDepartmentCategoriesPromise: Promise<ArrayResponse<DepartmentCategory>> | null = null;
  public fetching_my_company_product_roles: Promise<MyCompanyProductRoles> | null = null;
  public fetching_my_roles: Promise<MyRoles> | null = null;

  // other..
  private my_company_product_roles = new BehaviorSubject<MyCompanyProductRoles | null>(null);
  public myCompanyProductRolesObservable = this.my_company_product_roles.asObservable();
  private my_roles = new BehaviorSubject<MyRoles | null>(null);
  public myRolesObservable = this.my_roles.asObservable();

  my_company_product_teams: { [key: number]: CompanyProductTeam } = {};
  private userService = inject(UserService);
  private api = inject(ApiService);
  protected utils = inject(UtilsService);

  constructor() {
    this.initRolesAndDepartments();
    this.listenCurrentUser();
  }

  /**
   * OBSERVABLES METHODS
   */
  getCurrentDepartments() {
    return this.departments.value;
  }

  getCurrentRole() {
    return this.current_role;
  }

  getCurrentDepartmentCategory(): DepartmentCategory[] | undefined {
    return this.current_department_categories;
  }

  getCurrentDepartmentCategories() {
    return this.departmentCategories.value;
  }

  /**
   * ROLES METHODS
   */
  isDepartment(department_slug: string): boolean {
    if (this.departments.value == null) {
      return false;
    } else {
      if (this.current_departments != null && this.current_departments.length > 0) {
        return this.current_departments.map(d => d.slug).indexOf(department_slug) > -1;
      } else {
        return false;
      }
    }
  }

  isDepartmentCategory(department_category_slug: string): boolean {
    return this.current_department_categories?.find(dc => dc.slug == department_category_slug) != undefined;
  }

  validateDepartment(valid_departments: string[]) {
    return this.isValidDepartment(valid_departments);
  }

  validateDepartmentObservable(valid_departments: string[]): Observable<boolean> {
    return this.isValidDepartmentObservable(valid_departments);
  }

  isMyDepartmentSet() {
    return this.current_departments != null;
  }

  areCompanyProductUserMemberInitiated() {
    const product_roles_ready = this.my_company_product_roles != null && this.my_company_product_roles.value != null;
    const user = this.userService.getCurrentUser();
    const is_worker = user?.role?.slug === 'worker';

    if (!is_worker) {
      return product_roles_ready;
    }

    return product_roles_ready && this.my_roles.value != null;
  }

  hasCompanyProductMemberships(): boolean {
    const roles = this.my_company_product_roles.value;
    return roles != null && Object.keys(roles).length > 0;
  }

  getMyRoles(): MyRoles | null {
    return this.my_roles.value;
  }

  isAdmin() {
    return this.isValidDepartment(['direction']);
  }

  isBisualProductionTeamInCompanyProduct(company_product_id: string) {
    // Clients with an active CPUM must count as production team (CPUM overrides client_role).
    const member = this.my_company_product_roles.value?.[+company_product_id];
    return (member?.company_product_user_member_roles.length ?? 0) > 0;
  }

  isBisualProductionTeamInCompanyProductWithRole(
    company_product_id: string,
    role: 'tech-lead' | 'product-manager' | 'developer' | 'product-designer'
  ) {
    if (!this.isBisualProductionTeamInCompanyProduct(company_product_id)) return false;
    const roles = this.my_company_product_roles.value;
    if (!roles) return false;
    if ((+company_product_id) in roles) {
      const company_product_user_member = roles[+company_product_id];
      if (company_product_user_member == null) return false;
      const company_product_user_member_role_slugs = this.getCompanyProductRoleSlugs(company_product_user_member);
      return company_product_user_member_role_slugs.includes(role);
    } else return false;
  }

  isAccountManagerInCompanyProduct(company_product_id: string): boolean {
    const company_roles = this.my_roles.value;
    if (!company_roles) {
      return false;
    }

    const product_id = +company_product_id;

    return Object.values(company_roles).some(company => {
      const is_account_manager = company.roles.some(role => role.slug === CompanyUserMemberRoleEnum.ACCOUNT_MANAGER);
      if (!is_account_manager) {
        return false;
      }

      return (company.company_product_ids ?? []).includes(product_id);
    });
  }

  isBisualProductionTeamInCompanyProductAsTrio(company_product_id: string) {
    return (
      this.isBisualProductionTeamInCompanyProductWithRole(company_product_id, CompanyProductUserMemberRoleEnum.PRODUCT_MANAGER) ||
      this.isBisualProductionTeamInCompanyProductWithRole(company_product_id, CompanyProductUserMemberRoleEnum.TECH_LEAD) ||
      this.isBisualProductionTeamInCompanyProductWithRole(company_product_id, CompanyProductUserMemberRoleEnum.PRODUCT_DESIGNER)
    );
  }

  isProductTrioInAnyCompanyProduct(): boolean {
    const roles = this.my_company_product_roles.value;
    if (!roles) return false;

    const trio_slugs = [
      CompanyProductUserMemberRoleEnum.PRODUCT_MANAGER,
      CompanyProductUserMemberRoleEnum.TECH_LEAD,
      CompanyProductUserMemberRoleEnum.PRODUCT_DESIGNER,
    ];

    return Object.values(roles).some((companyProductRole: MyCompanyProductRoles[number]): boolean => {
      if (companyProductRole == null) {
        return false;
      }

      const company_product_user_member_role_slugs = this.getCompanyProductRoleSlugs(companyProductRole);
      return company_product_user_member_role_slugs.some(role_slug => trio_slugs.includes(role_slug as CompanyProductUserMemberRoleEnum));
    });
  }

  getProductTrioCompanyProductIds(): number[] {
    const roles = this.my_company_product_roles.value;
    if (!roles) return [];

    const trio_slugs = [
      CompanyProductUserMemberRoleEnum.PRODUCT_MANAGER,
      CompanyProductUserMemberRoleEnum.TECH_LEAD,
      CompanyProductUserMemberRoleEnum.PRODUCT_DESIGNER,
    ];

    return Object.values(roles)
      .filter((companyProductRole: MyCompanyProductRoles[number]): boolean => {
        if (companyProductRole == null) {
          return false;
        }

        const company_product_user_member_role_slugs = this.getCompanyProductRoleSlugs(companyProductRole);
        return company_product_user_member_role_slugs.some(role_slug => trio_slugs.includes(role_slug as CompanyProductUserMemberRoleEnum));
      })
      .map(companyProductRole => companyProductRole.company_product_id);
  }

  isTechLeadOrProductDesigner(company_product_id: string) {
    const is_tech_lead = this.isBisualProductionTeamInCompanyProductWithRole(
      company_product_id,
      CompanyProductUserMemberRoleEnum.TECH_LEAD
    );
    const is_product_designer = this.isBisualProductionTeamInCompanyProductWithRole(
      company_product_id,
      CompanyProductUserMemberRoleEnum.PRODUCT_DESIGNER
    );

    return is_tech_lead || is_product_designer;
  }

  isTechLeadOrProductDesignerWithoutProductManager(company_product_id: string) {
    const is_product_manager = this.isBisualProductionTeamInCompanyProductWithRole(
      company_product_id,
      CompanyProductUserMemberRoleEnum.PRODUCT_MANAGER
    );

    return !is_product_manager && this.isTechLeadOrProductDesigner(company_product_id);
  }

  isClient() {
    const user = this.userService.getCurrentUser();
    return user.company_id != null && user.role.slug === 'client';
  }

  isClientBusiness() {
    const user = this.userService.getCurrentUser();
    return user.company_id != null && user.role.slug === 'client' && user.client_role === 'business';
  }

  isClientDeveloper() {
    const user = this.userService.getCurrentUser();
    return user.company_id != null && user.role.slug === 'client' && user.client_role === 'developer';
  }

  isPartner() {
    const user = this.userService.getCurrentUser();
    return user.company_id != null && user.role.slug === 'partner';
  }

  isProductionTeamInCompanyProduct(company_product_id: string) {
    return this.isBisualProductionTeamInCompanyProduct(company_product_id) || this.isClientDeveloper();
  }

  isWorker() {
    const user = this.userService.getCurrentUser();
    return user.role.slug === 'worker';
  }

  isTechLead() {
    return this.current_department_categories?.find(dc => dc.slug.indexOf('tech-lead') > -1) != undefined;
  }

  isProductManager() {
    return this.current_department_categories?.find(dc => dc.slug.indexOf('product-manager') > -1) != undefined;
  }

  isWorkerDeveloper() {
    if (!this.isWorker() || !this.current_department_categories) {
      return false;
    }
    return this.current_department_categories.find(dc => dc.company_product_user_member_role?.slug === 'developer') != undefined;
  }

  isAccountManager(): boolean {
    const company_roles = this.my_roles.value;
    if (company_roles == null) {
      return false;
    }

    return Object.values(company_roles).some(company_role =>
      company_role.roles.some(role => role.slug === CompanyUserMemberRoleEnum.ACCOUNT_MANAGER)
    );
  }

  isInCompanyProductTeam(company_product_team_id: number) {
    return this.my_company_product_teams[company_product_team_id] != null;
  }

  /**
   * FUNCIONES PRIVADAS
   */
  private isValidDepartment(valid_departments: string[]): boolean {
    return this.current_departments != null && this.current_departments.length > 0
      ? this.current_departments.map(d => d.slug).filter(slug => valid_departments.includes(slug)).length > 0
      : false;
  }

  private isValidDepartmentObservable(valid_departments: string[]): Observable<boolean> {
    return from(this.current_departments_observable).pipe(
      map((departments: Department[]): boolean => {
        return departments != null && departments.length > 0
          ? departments.map(d => d.slug).filter(slug => valid_departments.includes(slug)).length > 0
          : false;
      })
    );
  }

  private getCompanyProductRoleSlugs(company_product_role: MyCompanyProductRoles[number] | null | undefined): string[] {
    if (company_product_role == null) {
      return [];
    }

    return company_product_role.company_product_user_member_roles.map(role => role.slug);
  }

  private listenCurrentUser() {
    this.userService.currentUser.subscribe(data => {
      // Siempre reseteamos el estado por-usuario para no arrastrar permisos de la sesión anterior
      // (logout/login o impersonación pasan por aquí vía UserService.setUser/deleteUser).
      this.resetUserScopedState();

      if (data != null) {
        this.current_role = data.role;
        if (this.current_role.slug === 'worker') {
          this.current_department_categories =
            data.current_working_contract?.current_working_contract_variable_condition?.department_categories;

          if (this.current_department_categories != null && this.current_department_categories.length > 0) {
            this.current_departments = [];
            const departments_ids_to_fetch: number[] = [...new Set(this.current_department_categories.map(dc => dc.department_id))];

            this.api.getDepartments({ id: departments_ids_to_fetch.join(',') }).subscribe(d => {
              this.current_departments = d.data;
              this.current_departments_subject.next(this.current_departments ?? []);
            });
          } else {
            this.current_departments = [];
            this.current_departments_subject.next([]);
          }
        } else if (this.current_role.slug === 'partner') {
          this.current_department_categories = undefined;
          const partner_departments = data.current_partner_contract?.current_partner_contract_variable_condition?.departments ?? [];

          this.current_departments = partner_departments;
          this.current_departments_subject.next(this.current_departments);
        } else {
          this.current_department_categories = undefined;
          this.current_departments = undefined;
          this.current_departments_subject.next([]);
        }
        this.refreshMyCompanyProductRoles(data);
        this.initMyCompanyProductTeams();
      } else {
        this.current_role = null;
        this.current_department_categories = undefined;
        this.current_departments = undefined;
        this.current_departments_subject.next([]);
      }
    });
  }

  /**
   * Limpia todo el estado que depende del usuario autenticado. Los catálogos globales
   * (roles, departments, departmentCategories) no se tocan porque no varían entre usuarios.
   */
  private resetUserScopedState() {
    this.current_role = null;
    this.current_department_categories = undefined;
    this.current_departments = undefined;
    this.current_departments_subject.next([]);
    this.my_company_product_teams = {};
    this.fetching_my_company_product_roles = null;
    this.my_company_product_roles.next(null);
    this.fetching_my_roles = null;
    this.my_roles.next(null);
  }

  private refreshMyCompanyProductRoles(user: User) {
    const is_worker = user.role.slug === 'worker';
    const is_partner = user.company_id != null && user.role.slug === 'partner';
    const is_client = user.company_id != null && user.role.slug === 'client';

    if (is_worker || is_partner || is_client) {
      this.fetching_my_company_product_roles = firstValueFrom(this.api.getMyCompanyProductRoles());
      this.fetching_my_company_product_roles.then(roles => {
        this.my_company_product_roles.next(roles);
      });
    }

    if (is_worker) {
      this.fetching_my_roles = firstValueFrom(this.api.getMyRoles());
      this.fetching_my_roles.then(roles => {
        this.my_roles.next(roles);
      });
    }
  }

  private initRolesAndDepartments() {
    this.fetchingDepartmentsPromise = firstValueFrom(this.api.getDepartments());
    this.fetchingDepartmentCategoriesPromise = firstValueFrom(this.api.getDepartmentCategories());
    this.fetchingRolesPromise = firstValueFrom(this.api.getRoles());
    this.fetchingDepartmentsPromise?.then(data => {
      const obj: { [key: string]: Department } = {};
      data.data.forEach(d => {
        obj[d.slug] = d;
      });
      this.departments.next(obj);
    });

    this.fetchingRolesPromise?.then(data => {
      const obj: { [key: string]: Role } = {};
      data.forEach(r => {
        obj[r.slug] = r;
      });
      this.roles.next(obj);
    });

    this.fetchingDepartmentCategoriesPromise?.then((response: ArrayResponse<DepartmentCategory>): void => {
      const obj: { [key: string]: DepartmentCategory } = {};
      const categoriesArray: DepartmentCategory[] = response.data || [];

      categoriesArray.forEach((r: DepartmentCategory): void => {
        obj[r.name] = r;
      });

      this.departmentCategories.next(obj);
    });
  }

  private initMyCompanyProductTeams() {
    this.api.getMyCompanyProductTeams().subscribe(data => {
      this.my_company_product_teams = {};
      data.forEach(pt => {
        this.my_company_product_teams[pt.id] = pt;
      });
    });
  }
}
